import React, { useState } from 'react';
import { offerOptions, needOptions } from '@ventureroom/core';
import { useApp } from './provider';
import { Page, Column, Input, Button, Copy, Row, MultiChoice } from './components';

type Mode = 'login' | 'signup' | 'pending' | 'forgot' | 'resend' | 'verify' | 'reset';
export function AccountScreen({
  link,
  token = '',
  onLinkDone,
}: {
  link?: 'verify' | 'reset';
  token?: string;
  onLinkDone?: () => void;
}) {
  const app = useApp();
  const [mode, setMode] = useState<Mode>(link ?? 'login');
  const [username, setUsername] = useState(''),
    [email, setEmail] = useState('');
  const [password, setPassword] = useState(''),
    [confirmPassword, setConfirmPassword] = useState('');
  const [offers, setOffers] = useState<string[]>([]),
    [needs, setNeeds] = useState<string[]>([]);
  const [message, setMessage] = useState(''),
    [validation, setValidation] = useState('');
  const titles = {
    login: 'Sign in',
    signup: 'Join VentureRoom',
    pending: 'Check your email',
    forgot: 'Forgot password',
    resend: 'Verify your email',
    verify: 'Verify your email',
    reset: 'Choose a new password',
  };
  const change = (next: Mode) => {
    setMode(next);
    setPassword('');
    setConfirmPassword('');
    setMessage('');
    setValidation('');
  };
  const done = () => {
    change('login');
    onLinkDone?.();
  };
  async function submit() {
    setValidation('');
    setMessage('');
    if ((mode === 'verify' || mode === 'reset') && !/^[a-f0-9]{64}$/.test(token)) {
      setValidation('This email link is invalid. Return to sign in to request a new email.');
      return;
    }
    if ((mode === 'signup' || mode === 'reset') && password !== confirmPassword) {
      setValidation('Passwords do not match.');
      return;
    }
    if (
      (mode === 'signup' || mode === 'reset') &&
      (password.length < 12 || password.length > 128)
    ) {
      setValidation('Use a password of 12–128 characters.');
      return;
    }
    if (mode === 'signup' && (!offers.length || !needs.length)) {
      setValidation('Select at least one thing you can offer and one thing you need.');
      return;
    }
    if (mode === 'login') {
      if (await app.authenticate(username, password)) setPassword('');
      return;
    }
    const result =
      mode === 'signup'
        ? await app.accountRequest('register', {
            username,
            email,
            password,
            confirmPassword,
            offers,
            needs,
          })
        : mode === 'verify'
          ? await app.accountRequest('verify-email', { token })
          : mode === 'reset'
            ? await app.accountRequest('reset-password', { token, password, confirmPassword })
            : await app.accountRequest(
                mode === 'forgot' ? 'forgot-password' : 'resend-verification',
                { email },
              );
    if (!result) return;
    if (mode === 'signup') {
      change('pending');
      setMessage('Open the verification email before signing in. The link expires in 24 hours.');
    } else setMessage(result.message ?? 'Check your inbox and spam folder.');
    setPassword('');
    setConfirmPassword('');
  }
  const hasPassword = ['login', 'signup', 'reset'].includes(mode);
  return (
    <Page title={titles[mode]}>
      <Column gap={18}>
        {['login', 'signup'].includes(mode) && (
          <Input label="Username" value={username} onChange={setUsername} />
        )}
        {['signup', 'forgot', 'resend', 'pending'].includes(mode) && (
          <Input label="Email" email value={email} onChange={setEmail} />
        )}
        {mode === 'signup' && (
          <>
            <MultiChoice
              label="I can offer"
              options={offerOptions}
              value={offers}
              onChange={setOffers}
              allowOther
            />
            <MultiChoice
              label="I need"
              options={needOptions}
              value={needs}
              onChange={setNeeds}
              allowOther
            />
          </>
        )}
        {hasPassword && (
          <Input
            label={mode === 'reset' ? 'New password' : 'Password'}
            secret
            value={password}
            onChange={setPassword}
            onSubmit={mode === 'login' ? () => void submit() : undefined}
          />
        )}
        {['signup', 'reset'].includes(mode) && (
          <>
            <Input
              label="Confirm password"
              secret
              value={confirmPassword}
              onChange={setConfirmPassword}
              onSubmit={() => void submit()}
            />
            <Copy muted>
              Use at least 12 characters. Your email is private and used for account verification
              and password recovery.
            </Copy>
          </>
        )}
        {mode === 'verify' && !message && (
          <Copy>Confirm this email address belongs to you to finish creating your account.</Copy>
        )}
        {mode === 'forgot' && (
          <Copy muted>
            We will send a one-use password reset link to your verified email address.
          </Copy>
        )}
        {!!message && <Copy>{message}</Copy>}
        {!!validation && <Copy>{validation}</Copy>}
        {!!app.error && <Copy>{app.error}</Copy>}
        <Row wrap>
          {!(message && (mode === 'verify' || mode === 'reset')) && (
            <Button disabled={app.busy} onPress={() => void submit()}>
              {app.busy
                ? 'Please wait…'
                : mode === 'signup'
                  ? 'Create account'
                  : mode === 'login'
                    ? 'Sign in'
                    : mode === 'verify'
                      ? 'Verify email'
                      : mode === 'reset'
                        ? 'Change password'
                        : mode === 'forgot'
                          ? 'Send reset email'
                          : 'Resend verification email'}
            </Button>
          )}
          {mode === 'login' ? (
            <>
              <Button kind="quiet" onPress={() => change('signup')}>
                Create an account
              </Button>
              <Button kind="quiet" onPress={() => change('forgot')}>
                Forgot password?
              </Button>
              <Button kind="quiet" onPress={() => change('resend')}>
                Resend verification email
              </Button>
            </>
          ) : (
            <Button kind="quiet" onPress={done}>
              Back to sign in
            </Button>
          )}
        </Row>
      </Column>
    </Page>
  );
}
